import mitt from 'mitt'

const emitter = mitt()

const DEBUG_EVENTS = false

export const POS_EVENTS = {
    ONLINE_ORDER_CREATED: 'online_order_created',
    ONLINE_ORDER_UPDATED: 'online_order_updated',
    ONLINE_ORDER_STATUS_CHANGED: 'online_order_status_changed',
    PAYMENT_CAPTURED: 'payment_captured',

    POS_ORDER_CREATED: 'pos_order_created',
    POS_ORDER_UPDATED: 'pos_order_updated',
    POS_ORDER_SENT_TO_KITCHEN: 'pos_order_sent_to_kitchen',
    POS_ORDER_ITEMS_ADDED: 'pos_order_items_added',
    POS_ORDER_ITEMS_REMOVED: 'pos_order_items_removed',
    POS_ORDER_ITEMS_UPDATED: 'pos_order_items_updated',
    POS_ORDER_STATUS_CHANGED: 'pos_order_status_changed',
    POS_ORDER_ITEM_READY: 'pos_order_item_ready',
    POS_ORDER_ITEM_SERVED: 'pos_order_item_served',
    POS_ORDER_READY: 'pos_order_ready',
    POS_ORDER_DISCARDED: 'pos_order_discarded',
    POS_ORDER_PAYMENT_COMPLETED: 'pos_order_payment_completed',
    POS_COURSE_FIRED: 'pos_course_fired',
    POS_COURSE_HELD: 'pos_course_held',

    KDS_HEARTBEAT: 'kds_heartbeat',
    KDS_ORDER_REFRESH: 'kds_order_refresh',

    ORDERS_SYNCED: 'orders_synced',
    SYNC_FAILED: 'sync_failed',

    TABLE_UPDATED: 'table_updated',
    TABLE_OCCUPIED: 'table_occupied',
    TABLE_FREED: 'table_freed',
    TABLE_REQUEST_NEW: 'table_request_new',
    TABLE_REQUEST_ACKNOWLEDGED: 'table_request_acknowledged',

    TAB_BILL_REQUESTED: 'tab_bill_requested',
    TAB_PAYMENT_RECEIVED: 'tab_payment_received',

    PAYMENT_COMPLETED: 'payment_completed',
    PAYMENT_FAILED: 'payment_failed',

    MESSAGE_FAILED: 'message_failed',
    MESSAGE_RETRY_FAILED: 'message_retry_failed',

    PATRONECT_CUSTOMER_ARRIVAL: 'patronect_customer_arrival',
    PATRONECT_FACE_FLAGGED: 'patronect_face_flagged',
    PATRONECT_FACE_DETECTED: 'patronect_face_detected',
    VIEW_CUSTOMER: 'view_customer',

    KIOSK_PENDING_ORDERS: 'kiosk_pending_orders',
    KIOSK_ORDER_RECOVERY_RESULT: 'kiosk_order_recovery_result',
    KIOSK_DISABLE_ORDER_TYPES: 'kiosk_disable_order_types',
    KIOSK_ORDERING_STATUS: 'kiosk_ordering_status',
    KIOSK_REFRESH_MENU: 'kiosk_refresh_menu',
    KIOSK_REQUEST_PENDING_ORDERS: 'kiosk_request_pending_orders',
    KIOSK_RECOVER_ORDER: 'kiosk_recover_order',
    DEVICE_DISABLED: 'device_disabled',
    DEVICE_ENABLED: 'device_enabled',
}

const logEvent = (eventName, payload) => {
    if (DEBUG_EVENTS) {
        console.log(`[POS Event Bus] 📡 ${eventName}`, payload)
    }
}

export function usePosEventBus() {
    const emit = (eventName, payload) => {
        logEvent(eventName, payload)
        emitter.emit(eventName, payload)
    }

    const on = (eventName, handler) => {
        emitter.on(eventName, handler)
    }

    const off = (eventName, handler) => {
        emitter.off(eventName, handler)
    }

    const offAll = (eventName) => {
        emitter.off(eventName)
    }

    const clearKioskHandlers = () => {
        emitter.off(POS_EVENTS.KIOSK_DISABLE_ORDER_TYPES)
        emitter.off(POS_EVENTS.KIOSK_ORDERING_STATUS)
        emitter.off(POS_EVENTS.KIOSK_REFRESH_MENU)
        emitter.off(POS_EVENTS.KIOSK_REQUEST_PENDING_ORDERS)
        emitter.off(POS_EVENTS.KIOSK_RECOVER_ORDER)
        emitter.off(POS_EVENTS.DEVICE_DISABLED)
        emitter.off(POS_EVENTS.DEVICE_ENABLED)
    }

    return {
        emitter,
        POS_EVENTS,
        emit,
        on,
        off,
        offAll,
        clearKioskHandlers,

        emitOnlineOrderCreated: (order) => emit(POS_EVENTS.ONLINE_ORDER_CREATED, order),
        onOnlineOrderCreated: (handler) => on(POS_EVENTS.ONLINE_ORDER_CREATED, handler),

        emitOnlineOrderUpdated: (order) => emit(POS_EVENTS.ONLINE_ORDER_UPDATED, order),
        onOnlineOrderUpdated: (handler) => on(POS_EVENTS.ONLINE_ORDER_UPDATED, handler),

        emitOnlineOrderStatusChanged: (orderId, status) => emit(POS_EVENTS.ONLINE_ORDER_STATUS_CHANGED, {orderId, status}),
        onOnlineOrderStatusChanged: (handler) => on(POS_EVENTS.ONLINE_ORDER_STATUS_CHANGED, handler),

        emitPosOrderCreated: (order) => emit(POS_EVENTS.POS_ORDER_CREATED, order),
        onPosOrderCreated: (handler) => on(POS_EVENTS.POS_ORDER_CREATED, handler),

        emitPosOrderUpdated: (order) => emit(POS_EVENTS.POS_ORDER_UPDATED, order),
        onPosOrderUpdated: (handler) => on(POS_EVENTS.POS_ORDER_UPDATED, handler),

        emitPosOrderSentToKitchen: (orderId, orderNumber) => emit(POS_EVENTS.POS_ORDER_SENT_TO_KITCHEN, {orderId, orderNumber}),
        onPosOrderSentToKitchen: (handler) => on(POS_EVENTS.POS_ORDER_SENT_TO_KITCHEN, handler),

        emitPosOrderItemsAdded: (orderId, items) => emit(POS_EVENTS.POS_ORDER_ITEMS_ADDED, {orderId, items}),
        onPosOrderItemsAdded: (handler) => on(POS_EVENTS.POS_ORDER_ITEMS_ADDED, handler),

        emitPosOrderItemsRemoved: (orderId, items) => emit(POS_EVENTS.POS_ORDER_ITEMS_REMOVED, {orderId, items}),
        onPosOrderItemsRemoved: (handler) => on(POS_EVENTS.POS_ORDER_ITEMS_REMOVED, handler),

        emitPosOrderItemsUpdated: (orderId, items) => emit(POS_EVENTS.POS_ORDER_ITEMS_UPDATED, {orderId, items}),
        onPosOrderItemsUpdated: (handler) => on(POS_EVENTS.POS_ORDER_ITEMS_UPDATED, handler),

        emitPosOrderStatusChanged: (orderId, status) => emit(POS_EVENTS.POS_ORDER_STATUS_CHANGED, {orderId, status}),
        onPosOrderStatusChanged: (handler) => on(POS_EVENTS.POS_ORDER_STATUS_CHANGED, handler),

        emitPosOrderItemReady: (orderId, itemIndex, itemName, orderNumber) => emit(POS_EVENTS.POS_ORDER_ITEM_READY, {orderId, itemIndex, itemName, orderNumber}),
        onPosOrderItemReady: (handler) => on(POS_EVENTS.POS_ORDER_ITEM_READY, handler),

        emitPosOrderItemServed: (orderId, itemIndex, itemName, orderNumber) => emit(POS_EVENTS.POS_ORDER_ITEM_SERVED, {orderId, itemIndex, itemName, orderNumber}),
        onPosOrderItemServed: (handler) => on(POS_EVENTS.POS_ORDER_ITEM_SERVED, handler),

        emitPosOrderReady: (orderId, orderNumber, orderType, tableNumber) => emit(POS_EVENTS.POS_ORDER_READY, {orderId, orderNumber, orderType, tableNumber}),
        onPosOrderReady: (handler) => on(POS_EVENTS.POS_ORDER_READY, handler),

        emitPosOrderDiscarded: (orderId, tableId, orderType) => emit(POS_EVENTS.POS_ORDER_DISCARDED, {orderId, tableId, orderType}),
        onPosOrderDiscarded: (handler) => on(POS_EVENTS.POS_ORDER_DISCARDED, handler),

        emitPosCourseFired: (orderId, courseId, courseName) => emit(POS_EVENTS.POS_COURSE_FIRED, {orderId, courseId, courseName}),
        onPosCourseFired: (handler) => on(POS_EVENTS.POS_COURSE_FIRED, handler),

        emitPosCourseHeld: (orderId, courseId, courseName) => emit(POS_EVENTS.POS_COURSE_HELD, {orderId, courseId, courseName}),
        onPosCourseHeld: (handler) => on(POS_EVENTS.POS_COURSE_HELD, handler),

        emitKdsHeartbeat: (kdsDeviceId, businessId) => emit(POS_EVENTS.KDS_HEARTBEAT, {deviceId: kdsDeviceId, businessId, timestamp: Date.now()}),
        onKdsHeartbeat: (handler) => on(POS_EVENTS.KDS_HEARTBEAT, handler),
        offKdsHeartbeat: (handler) => off(POS_EVENTS.KDS_HEARTBEAT, handler),

        emitKdsOrderRefresh: (order) => emit(POS_EVENTS.KDS_ORDER_REFRESH, {order}),
        onKdsOrderRefresh: (handler) => on(POS_EVENTS.KDS_ORDER_REFRESH, handler),

        emitOrdersSynced: (successCount, failCount, total) => emit(POS_EVENTS.ORDERS_SYNCED, {successCount, failCount, total}),
        onOrdersSynced: (handler) => on(POS_EVENTS.ORDERS_SYNCED, handler),

        emitSyncFailed: (error) => emit(POS_EVENTS.SYNC_FAILED, {error}),
        onSyncFailed: (handler) => on(POS_EVENTS.SYNC_FAILED, handler),

        emitTableUpdated: (tableId, tableName, status) => emit(POS_EVENTS.TABLE_UPDATED, {tableId, tableName, status}),
        onTableUpdated: (handler) => on(POS_EVENTS.TABLE_UPDATED, handler),

        emitTableOccupied: (tableId, tableName) => emit(POS_EVENTS.TABLE_OCCUPIED, {tableId, tableName}),
        onTableOccupied: (handler) => on(POS_EVENTS.TABLE_OCCUPIED, handler),

        emitTableFreed: (tableId, tableName) => emit(POS_EVENTS.TABLE_FREED, {tableId, tableName}),
        onTableFreed: (handler) => on(POS_EVENTS.TABLE_FREED, handler),

        emitTableRequestNew: (payload) => emit(POS_EVENTS.TABLE_REQUEST_NEW, payload),
        onTableRequestNew: (handler) => {
            on(POS_EVENTS.TABLE_REQUEST_NEW, handler)
            return () => off(POS_EVENTS.TABLE_REQUEST_NEW, handler)
        },

        emitTableRequestAcknowledged: (payload) => emit(POS_EVENTS.TABLE_REQUEST_ACKNOWLEDGED, payload),
        onTableRequestAcknowledged: (handler) => {
            on(POS_EVENTS.TABLE_REQUEST_ACKNOWLEDGED, handler)
            return () => off(POS_EVENTS.TABLE_REQUEST_ACKNOWLEDGED, handler)
        },

        emitTabBillRequested: (payload) => emit(POS_EVENTS.TAB_BILL_REQUESTED, payload),
        onTabBillRequested: (handler) => {
            on(POS_EVENTS.TAB_BILL_REQUESTED, handler)
            return () => off(POS_EVENTS.TAB_BILL_REQUESTED, handler)
        },

        emitTabPaymentReceived: (payload) => emit(POS_EVENTS.TAB_PAYMENT_RECEIVED, payload),
        onTabPaymentReceived: (handler) => {
            on(POS_EVENTS.TAB_PAYMENT_RECEIVED, handler)
            return () => off(POS_EVENTS.TAB_PAYMENT_RECEIVED, handler)
        },

        emitPaymentCompleted: (orderId, amount, paymentMethod) => emit(POS_EVENTS.PAYMENT_COMPLETED, {orderId, amount, paymentMethod}),
        onPaymentCompleted: (handler) => on(POS_EVENTS.PAYMENT_COMPLETED, handler),

        emitPaymentFailed: (orderId, error) => emit(POS_EVENTS.PAYMENT_FAILED, {orderId, error}),
        onPaymentFailed: (handler) => on(POS_EVENTS.PAYMENT_FAILED, handler),

        emitPaymentCaptured: (orderId, payload) => emit(POS_EVENTS.PAYMENT_CAPTURED, {orderId, ...payload}),
        onPaymentCaptured: (handler) => on(POS_EVENTS.PAYMENT_CAPTURED, handler),
        offPaymentCaptured: (handler) => off(POS_EVENTS.PAYMENT_CAPTURED, handler),

        emitMessageFailed: (uuid, eventName, targetDevices, failureReason) => emit(POS_EVENTS.MESSAGE_FAILED, {uuid, eventName, targetDevices, failureReason}),
        onMessageFailed: (handler) => on(POS_EVENTS.MESSAGE_FAILED, handler),

        emitMessageRetryFailed: (uuid, eventName, retryCount) => emit(POS_EVENTS.MESSAGE_RETRY_FAILED, {uuid, eventName, retryCount}),
        onMessageRetryFailed: (handler) => on(POS_EVENTS.MESSAGE_RETRY_FAILED, handler),

        emitPatronectCustomerArrival: (payload) => emit(POS_EVENTS.PATRONECT_CUSTOMER_ARRIVAL, payload),
        onPatronectCustomerArrival: (handler) => on(POS_EVENTS.PATRONECT_CUSTOMER_ARRIVAL, handler),
        offPatronectCustomerArrival: (handler) => off(POS_EVENTS.PATRONECT_CUSTOMER_ARRIVAL, handler),

        emitPatronectFaceFlagged: (payload) => emit(POS_EVENTS.PATRONECT_FACE_FLAGGED, payload),
        onPatronectFaceFlagged: (handler) => on(POS_EVENTS.PATRONECT_FACE_FLAGGED, handler),
        offPatronectFaceFlagged: (handler) => off(POS_EVENTS.PATRONECT_FACE_FLAGGED, handler),

        emitPatronectFaceDetected: (payload) => emit(POS_EVENTS.PATRONECT_FACE_DETECTED, payload),
        onPatronectFaceDetected: (handler) => on(POS_EVENTS.PATRONECT_FACE_DETECTED, handler),
        offPatronectFaceDetected: (handler) => off(POS_EVENTS.PATRONECT_FACE_DETECTED, handler),

        emitViewCustomer: (customer) => emit(POS_EVENTS.VIEW_CUSTOMER, customer),
        onViewCustomer: (handler) => on(POS_EVENTS.VIEW_CUSTOMER, handler),
        offViewCustomer: (handler) => off(POS_EVENTS.VIEW_CUSTOMER, handler),

        emitKioskPendingOrders: (payload) => emit(POS_EVENTS.KIOSK_PENDING_ORDERS, payload),
        onKioskPendingOrders: (handler) => on(POS_EVENTS.KIOSK_PENDING_ORDERS, handler),
        offKioskPendingOrders: (handler) => off(POS_EVENTS.KIOSK_PENDING_ORDERS, handler),

        emitKioskOrderRecoveryResult: (payload) => emit(POS_EVENTS.KIOSK_ORDER_RECOVERY_RESULT, payload),
        onKioskOrderRecoveryResult: (handler) => on(POS_EVENTS.KIOSK_ORDER_RECOVERY_RESULT, handler),
        offKioskOrderRecoveryResult: (handler) => off(POS_EVENTS.KIOSK_ORDER_RECOVERY_RESULT, handler),

        emitKioskDisableOrderTypes: (payload) => emit(POS_EVENTS.KIOSK_DISABLE_ORDER_TYPES, payload),
        onKioskDisableOrderTypes: (handler) => on(POS_EVENTS.KIOSK_DISABLE_ORDER_TYPES, handler),
        offKioskDisableOrderTypes: (handler) => off(POS_EVENTS.KIOSK_DISABLE_ORDER_TYPES, handler),

        emitKioskOrderingStatus: (payload) => emit(POS_EVENTS.KIOSK_ORDERING_STATUS, payload),
        onKioskOrderingStatus: (handler) => on(POS_EVENTS.KIOSK_ORDERING_STATUS, handler),
        offKioskOrderingStatus: (handler) => off(POS_EVENTS.KIOSK_ORDERING_STATUS, handler),

        emitKioskRefreshMenu: (payload) => emit(POS_EVENTS.KIOSK_REFRESH_MENU, payload),
        onKioskRefreshMenu: (handler) => on(POS_EVENTS.KIOSK_REFRESH_MENU, handler),
        offKioskRefreshMenu: (handler) => off(POS_EVENTS.KIOSK_REFRESH_MENU, handler),

        emitKioskRequestPendingOrders: (payload) => emit(POS_EVENTS.KIOSK_REQUEST_PENDING_ORDERS, payload),
        onKioskRequestPendingOrders: (handler) => on(POS_EVENTS.KIOSK_REQUEST_PENDING_ORDERS, handler),
        offKioskRequestPendingOrders: (handler) => off(POS_EVENTS.KIOSK_REQUEST_PENDING_ORDERS, handler),

        emitKioskRecoverOrder: (payload) => emit(POS_EVENTS.KIOSK_RECOVER_ORDER, payload),
        onKioskRecoverOrder: (handler) => on(POS_EVENTS.KIOSK_RECOVER_ORDER, handler),
        offKioskRecoverOrder: (handler) => off(POS_EVENTS.KIOSK_RECOVER_ORDER, handler),

        emitDeviceDisabled: (payload) => emit(POS_EVENTS.DEVICE_DISABLED, payload),
        onDeviceDisabled: (handler) => on(POS_EVENTS.DEVICE_DISABLED, handler),
        offDeviceDisabled: (handler) => off(POS_EVENTS.DEVICE_DISABLED, handler),

        emitDeviceEnabled: (payload) => emit(POS_EVENTS.DEVICE_ENABLED, payload),
        onDeviceEnabled: (handler) => on(POS_EVENTS.DEVICE_ENABLED, handler),
        offDeviceEnabled: (handler) => off(POS_EVENTS.DEVICE_ENABLED, handler),
    }
}
